import { useCallback, useEffect, useState } from 'react';
import {
  FrejaAuthType,
  FrejaEIDStatus,
  cancelFrejaEIdAuth,
  collectFrejaEIdAuthState,
  finishFrejaEIdAuth,
  initiateFrejaEIdAuth
} from '@api/auth/freja-eid';
import { useSession } from '@lib/useSession';
import { AxiosError } from 'axios';
import { ICustomerBase, IMaintenance, isMaintenance } from '@api/auth/session';
import { useTranslation } from 'react-i18next';

interface IAuthData {
  authRef: string;
  qrCode?: string;
  status?: FrejaEIDStatus;
  autoStart?: boolean;
}

type AuthCompletedFn = (
  customers: ICustomerBase[],
  completeAuth: (customerId: number) => Promise<void>
) => void;

const COLLECT_TIMEOUT = 2000;

export function useFrejaEIdAuth(onAuthCompleted: AuthCompletedFn) {
  const { t } = useTranslation();
  const { updateSession } = useSession();
  const [authData, setAuthData] = useState<IAuthData>();
  const [maintenanceInfo, setMaintenanceInfo] = useState<IMaintenance | null>(null);

  async function initiateAuth(authType?: FrejaAuthType, autoStart?: boolean) {
    setMaintenanceInfo(null);
    const data = await initiateFrejaEIdAuth(authType ?? FrejaAuthType.Personal);
    setAuthData({
      authRef: data.AuthRef,
      autoStart,
      qrCode: data.QrCode,
      status: FrejaEIDStatus.Started
    });

    if (autoStart) {
      window.location.replace(
        `frejaeid://bindUserToTransaction?transactionReference=${data.AuthRef}&originAppScheme=&redirect=null`
      );
    }
  }

  async function cancelAuth() {
    if (!authData) return;
    await cancelFrejaEIdAuth(authData.authRef);
    setAuthData(undefined);
  }

  async function finishAuth(customerId: number) {
    if (!authData) return;

    try {
      const session = await finishFrejaEIdAuth({ authRef: authData.authRef, customerId });

      updateSession(session);
    } catch (e) {
      const error = e as AxiosError;
      if (error.response && isMaintenance(error.response.data)) {
        setMaintenanceInfo(error.response.data);
        setAuthData(undefined);
      } else {
        setAuthData(undefined);
        throw e;
      }
    }
  }

  const getStatusMessage = useCallback(() => {
    if (!authData) return '';

    const { autoStart, status } = authData;

    switch (status) {
      case FrejaEIDStatus.Started:
        return autoStart ? t('login:freja:mobileStarted') : t('login:freja:started');
      case FrejaEIDStatus.DeliveredToMobile:
        return t('login:freja:deliveredToMobile');
      case FrejaEIDStatus.Canceled:
        return t('login:freja:canceled');
      case FrejaEIDStatus.RP_Canceled:
        return t('login:freja:rpCanceled');
      case FrejaEIDStatus.Expired:
        return t('login:freja:expired');
      case FrejaEIDStatus.Approved:
        return t('login:freja:approved');
      case FrejaEIDStatus.Rejected:
        return t('login:freja:rejected');
      default: {
        return '';
      }
    }
  }, [authData?.status, authData?.autoStart, t]);

  useEffect(() => {
    if (!authData) return;

    const { authRef } = authData;

    let timerId = 0;
    const collect = async () => {
      try {
        const state = await collectFrejaEIdAuthState(authRef);
        setAuthData(current => {
          if (!current) return current;

          if (state.Status === FrejaEIDStatus.Approved) {
            setAuthData({
              ...current,
              status: state.Status,
              qrCode: undefined
            });

            onAuthCompleted(state.Customers, finishAuth);
          } else {
            setAuthData({
              ...current,
              status: state.Status,
              ...(state.Status !== FrejaEIDStatus.Started &&
                state.Status !== FrejaEIDStatus.DeliveredToMobile && { qrCode: undefined })
            });
          }

          return current;
        });

        if (
          state.Status !== FrejaEIDStatus.Started &&
          state.Status !== FrejaEIDStatus.DeliveredToMobile
        ) {
          window.clearInterval(timerId);
          timerId = 0;
        }
      } catch {
        // Ignore any error
      }
    };

    timerId = window.setInterval(collect, COLLECT_TIMEOUT);
    return () => {
      if (timerId !== 0) {
        window.clearInterval(timerId);
        timerId = 0;
      }
    };
  }, [authData?.authRef, authData?.status]);

  return {
    authData,
    setAuthData,
    initiateAuth,
    cancelAuth,
    finishAuth,
    getStatusMessage,
    maintenanceInfo
  };
}
